import { Divider } from 'antd';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import type { SimulationFilterEditedAction } from 'types/ppaSimulationMeta';
import type { SimulatorAppliedFilters, SimulatorFilterCategory } from './SimulatorFilterPanel';
import SimulatorAppliedFiltersDetailPanel from './SimulatorAppliedFiltersDetailPanel';
import { buildSimulatorFilterDetailGroups } from './simulatorFilter.helpers';
import {
    StyledAppliedFiltersBar,
    StyledAppliedFiltersDetailOverlay,
    StyledAppliedFiltersSection,
} from './SimulatorAppliedFiltersBar.styles';

export type AppliedSimulatorFilterGroup = {
    key: string;
    label: string;
    count: number;
};

type Props = {
    filterCategories: SimulatorFilterCategory[];
    editedActionTypes?: SimulationFilterEditedAction[];
    appliedFilters: SimulatorAppliedFilters;
    isApplyLoading?: boolean;
    onAppliedFiltersChange: (filters: SimulatorAppliedFilters) => void | Promise<boolean>;
};

const buildAppliedFilterGroups = (
    filterCategories: SimulatorFilterCategory[],
    appliedFilters: SimulatorAppliedFilters,
    editedActionTypes: SimulationFilterEditedAction[] = [],
): AppliedSimulatorFilterGroup[] =>
    buildSimulatorFilterDetailGroups(
        filterCategories,
        appliedFilters.categorySelections,
        appliedFilters.showOnly,
        editedActionTypes,
    ).map((group) => ({
        key: group.key,
        label: group.label,
        count: group.items.length,
    }));

const SimulatorAppliedFiltersBar = ({
    filterCategories,
    editedActionTypes = [],
    appliedFilters,
    isApplyLoading = false,
    onAppliedFiltersChange,
}: Props) => {
    const sectionRef = useRef<HTMLDivElement>(null);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [draftFilters, setDraftFilters] = useState<SimulatorAppliedFilters>(appliedFilters);
    const prevAppliedFilterGroupCountRef = useRef(0);

    useEffect(() => {
        if (isDetailOpen) {
            setDraftFilters(appliedFilters);
        }
    }, [appliedFilters, isDetailOpen]);

    const appliedFilterGroups = useMemo(
        () => buildAppliedFilterGroups(filterCategories, appliedFilters, editedActionTypes),
        [appliedFilters, editedActionTypes, filterCategories],
    );

    const hasAppliedFilters = appliedFilterGroups.length > 0;

    useEffect(() => {
        const groupCount = appliedFilterGroups.length;
        if (groupCount > 0 && prevAppliedFilterGroupCountRef.current === 0) {
            setDraftFilters(appliedFilters);
            setIsDetailOpen(true);
        }
        prevAppliedFilterGroupCountRef.current = groupCount;
    }, [appliedFilterGroups.length, appliedFilters]);

    useEffect(() => {
        if (!hasAppliedFilters) {
            prevAppliedFilterGroupCountRef.current = 0;
            setIsDetailOpen(false);
        }
    }, [hasAppliedFilters]);

    useEffect(() => {
        if (!isDetailOpen) {
            return undefined;
        }

        const handleOutsideMouseDown = (event: MouseEvent) => {
            const target = event.target;
            if (!(target instanceof Node) || sectionRef.current?.contains(target)) {
                return;
            }
            setDraftFilters(appliedFilters);
            setIsDetailOpen(false);
        };

        document.addEventListener('mousedown', handleOutsideMouseDown);
        return () => document.removeEventListener('mousedown', handleOutsideMouseDown);
    }, [appliedFilters, isDetailOpen]);

    const closeDetailPanel = () => {
        setIsDetailOpen(false);
    };

    const openDetailPanel = () => {
        if (!hasAppliedFilters) {
            return;
        }
        setDraftFilters(appliedFilters);
        setIsDetailOpen(true);
    };

    const handleDetailApply = async () => {
        await onAppliedFiltersChange(draftFilters);
    };

    const handleDetailCancel = () => {
        setDraftFilters(appliedFilters);
        closeDetailPanel();
    };

    if (!hasAppliedFilters) {
        return null;
    }

    return (
        <StyledAppliedFiltersSection ref={sectionRef}>
            {!isDetailOpen ? (
                <StyledAppliedFiltersBar
                    className="is-clickable"
                    onClick={openDetailPanel}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            openDetailPanel();
                        }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-expanded={isDetailOpen}
                    aria-haspopup="dialog"
                >
                    <span className="applied-filters-label">Applied Filters:</span>
                    <div className="applied-filters-items">
                        {appliedFilterGroups.map((group, index) => (
                            <Fragment key={group.key}>
                                {index > 0 ? (
                                    <Divider type="vertical" className="applied-filters-divider" />
                                ) : null}
                                <span className="applied-filters-item">
                                    {group.label} ({group.count})
                                </span>
                            </Fragment>
                        ))}
                    </div>
                </StyledAppliedFiltersBar>
            ) : null}
            {isDetailOpen ? (
                <StyledAppliedFiltersDetailOverlay role="dialog" aria-label="Applied Filters">
                    <SimulatorAppliedFiltersDetailPanel
                        filterCategories={filterCategories}
                        editedActionTypes={editedActionTypes}
                        draftFilters={draftFilters}
                        isApplyLoading={isApplyLoading}
                        onDraftChange={setDraftFilters}
                        onClose={handleDetailCancel}
                        onCancel={handleDetailCancel}
                        onApply={() => void handleDetailApply()}
                    />
                </StyledAppliedFiltersDetailOverlay>
            ) : null}
        </StyledAppliedFiltersSection>
    );
};

export default SimulatorAppliedFiltersBar;
